import React from 'react';
import { Handle, NodeProps, Position } from '@xyflow/react';
import {
  badgeClass,
  handleClass,
  handleRow,
  nodeBody,
  nodeHeader,
  nodeShell,
  nodeShellIdle,
  nodeShellSelected,
} from '../../canvas/nodes/nodeStyles';
import {
  REVIEW_CORE_ANY_SLOT_HANDLE,
  REVIEW_CORE_RULING_HANDLE,
  ReviewerCoreNodeData,
  ReviewerStatus,
} from '../graphTypes';
import {
  StatusGlyph,
  reviewHandleClass,
  reviewerStatus,
  statusOpacity,
  traceOutcomeBadgeClass,
} from './reviewerNodeStyles';

/**
 * `check → confirm → pair`, each with its model and (on a run) its outcome.
 *
 * `status` is OPTIONAL and absent means "this profile does not configure this
 * tier" — an observed-only row. Such a row gets no switch glyph at all rather
 * than a fabricated one: the run proves the tier ran, and nothing in the
 * profile says whether it is switched on, available or inherited.
 */
const TierRow: React.FC<{
  id: string;
  status?: ReviewerStatus;
  inherited?: boolean;
  model?: string;
  outcome?: string;
}> = ({ id, status, inherited, model, outcome }) => (
  <div
    className="flex items-center gap-1"
    data-testid={`reviewer-tier-${id}`}
    data-configured={status ? 'true' : 'false'}
  >
    {status ? (
      <StatusGlyph status={status} label={id} />
    ) : (
      <span className={badgeClass} title="not configured here — it ran on this run">
        observed
      </span>
    )}
    <span className="text-[10px] uppercase tracking-wider text-[var(--app-text)]">{id}</span>
    {model && (
      <span className="truncate font-mono text-[9px] text-[var(--app-text-muted)]" title={model}>
        {model}
      </span>
    )}
    {inherited && <span className={badgeClass}>inherited</span>}
    {outcome && (
      <span
        className={`${traceOutcomeBadgeClass(outcome)} ml-auto`}
        data-testid={`reviewer-tier-outcome-${id}`}
      >
        {outcome}
      </span>
    )}
  </div>
);

/**
 * The judge itself. Every `{{slot}}` its template asks for is a PORT on this
 * node, so a source connects to the very placeholder it fills and the picture
 * answers "what does the reviewer actually read?" without a legend.
 *
 * The body says only what a judge call is made of: which tiers run and on what
 * model, and the gates that decide when a cheap-tier fail escalates. On a run
 * (traces supplied), each tier also carries its LAST outcome.
 */
const ReviewerCoreNode: React.FC<NodeProps> = ({ data, selected }) => {
  const nodeData = data as ReviewerCoreNodeData;
  const { checkpoint, mode, ports, tierOutcomes } = nodeData;
  const { gates, reviewed } = checkpoint;

  // The backend fills every section of an UNREVIEWED checkpoint from the
  // resolved defaults, so this node would otherwise draw a fully live reviewer
  // for something that reviews nothing. The whole node is dimmed and says why:
  // it is a preview of what switching the reviewer on would look like.
  const reason = checkpoint.reason || 'the reviewer does not run here';

  // A tier the run exercised that this profile does not list (a `pick` between
  // candidates, say) still gets a row: it happened, so hiding it would lie.
  const listed = new Set(checkpoint.tiers.map((tier) => tier.id));
  const extraTiers = Object.keys(tierOutcomes).filter((tier) => !listed.has(tier));

  return (
    <div
      className={`${nodeShell} ${selected ? nodeShellSelected : nodeShellIdle} ${statusOpacity(
        reviewed ? 'on' : 'unavailable'
      )}`}
      style={{ borderLeft: '3px solid var(--accent-strong)' }}
      data-testid="reviewer-node-core"
      data-mode={mode}
      data-reviewed={reviewed ? 'true' : 'false'}
    >
      <div className={nodeHeader}>
        <span>Reviewer</span>
        {reviewed ? (
          <span className={`${badgeClass} ml-auto`} title={`review mode: ${mode}`}>
            {mode}
          </span>
        ) : (
          <span
            className={`${badgeClass} ml-auto`}
            data-testid="reviewer-core-unreviewed"
            title={reason}
          >
            not reviewed
          </span>
        )}
      </div>

      <div className={nodeBody}>
        {checkpoint.tiers.map((tier) => (
          <TierRow
            key={tier.id}
            id={tier.id}
            status={reviewed ? reviewerStatus(tier.enabled, tier.available) : 'off'}
            inherited={tier.inherited}
            model={tier.model}
            outcome={tierOutcomes[tier.id]}
          />
        ))}
        {extraTiers.map((tier) => (
          <TierRow key={tier} id={tier} outcome={tierOutcomes[tier]} />
        ))}
        <div
          className="font-mono text-[9px] uppercase tracking-wider"
          data-testid="reviewer-gates"
          title="escalates above this confidence, at this severity, this many times"
        >
          {`≥ ${gates.confidence.toFixed(2)} · ${gates.severity} · ${gates.max_attempts}×`}
        </div>
      </div>

      <div className="border-t border-[var(--border-subtle)] px-2.5 py-1.5 space-y-0.5">
        {ports.map((port) => (
          <div
            key={port.handle}
            className={handleRow}
            data-testid={`reviewer-port-${port.name}`}
            data-filled={port.filled ? 'true' : 'false'}
            data-pair-only={port.pairOnly ? 'true' : 'false'}
            title={port.note || `{{${port.name}}} · ${port.source}`}
          >
            <Handle
              type="target"
              position={Position.Left}
              id={port.handle}
              className={handleClass}
              isConnectable={false}
            />
            <span className={`truncate ${port.filled ? '' : 'line-through opacity-60'}`}>
              {port.name}
            </span>
            {port.pairOnly && <span className="ml-auto text-[8px] opacity-70">pair</span>}
          </div>
        ))}
      </div>

      {/* Everything that reaches the judge without a placeholder of its own. */}
      <Handle
        type="target"
        position={Position.Left}
        id={REVIEW_CORE_ANY_SLOT_HANDLE}
        className={reviewHandleClass}
        isConnectable={false}
      />
      <Handle
        type="source"
        position={Position.Right}
        id={REVIEW_CORE_RULING_HANDLE}
        className={reviewHandleClass}
        isConnectable={false}
      />
    </div>
  );
};

export default ReviewerCoreNode;
